/**
 * SharePage.tsx  — Phase 6
 * 路由 /share/:shareId       → DownloadSharePage（文件/文件夹下载分享）
 * 路由 /upload/:uploadToken  → UploadLinkPage（无账号上传）
 */

import { useI18n } from '../i18n';
import LanguageSelect from '../components/ui/LanguageSelect';
import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { shareApi, type ShareChildFile, type UploadLinkInfo } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FileIcon } from '@/components/files/FileIcon';
import { formatBytes, formatDate, decodeFileName } from '@/utils';
import { isPreviewable } from '@/utils/fileTypes';
import { ShareFilePreview } from '@/components/share/ShareFilePreview';
import {
  Download,
  Lock,
  Clock,
  AlertCircle,
  HardDrive,
  ShieldCheck,
  FolderOpen,
  Archive,
  CheckSquare,
  Square,
  Upload,
  CheckCircle2,
  XCircle,
  Loader2,
  File,
  Eye,
  ChevronRight,
  Home,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
export default function SharePage() {
  const { shareId, uploadToken } = useParams<{ shareId?: string; uploadToken?: string }>();
  if (uploadToken) return <UploadLinkPage token={uploadToken} />;
  if (shareId) return <DownloadSharePage shareId={shareId} />;
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Common shell
// ─────────────────────────────────────────────────────────────────────────────
function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b px-6 py-4 flex items-center gap-3">
        <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
          <HardDrive className="h-4 w-4 text-primary-foreground" />
        </div>
        <span className="font-semibold text-sm">OSSshelf</span>
        <span className="text-muted-foreground text-sm ml-1">· {title}</span>
        <div className="ml-auto"><LanguageSelect /></div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4">{children}</div>
      </main>
      <footer className="border-t px-6 py-3 text-center text-xs text-muted-foreground">
        {t("Powered by OSSshelf · 多厂商 OSS 文件管理系统")}</footer>
    </div>
  );
}

function PasswordPrompt({ isWrong, onSubmit }: { isWrong: boolean; onSubmit: (pw: string) => void }) {
  const { t } = useI18n();
  const [pw, setPw] = useState('');
  return (
    <div className="bg-card border rounded-xl p-8 space-y-5">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Lock className="h-7 w-7 text-primary" />
        </div>
        <h1 className="text-lg font-semibold">{t("需要访问密码")}</h1>
        <p className="text-sm text-muted-foreground">{t("该内容已加密，请输入密码继续")}</p>
      </div>
      <div className="space-y-3">
        <Input
          type="password"
          placeholder={t("输入访问密码")}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit(pw)}
          autoFocus
        />
        {isWrong && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> {t("密码错误，请重试")}</p>
        )}
        <Button className="w-full" onClick={() => onSubmit(pw)} disabled={!pw.trim()}>
          <ShieldCheck className="h-4 w-4 mr-2" /> {t("验证密码")}</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DownloadSharePage
// ─────────────────────────────────────────────────────────────────────────────
function DownloadSharePage({ shareId }: { shareId: string }) {
  const { t } = useI18n();
  const [enteredPw, setEnteredPw] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<{
    id: string;
    name: string;
    size: number;
    mimeType: string | null;
  } | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<Array<{ id: string; name: string }>>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['share-public', shareId, enteredPw],
    queryFn: () => shareApi.get(shareId, enteredPw).then((r) => r.data),
    enabled: !!shareId,
    retry: false,
  });

  const { data: folderData, isLoading: folderLoading } = useQuery({
    queryKey: ['share-folder', shareId, currentFolderId, enteredPw],
    queryFn: () => shareApi.getFolder(shareId, currentFolderId!, enteredPw).then((r) => r.data),
    enabled: !!currentFolderId && !!data?.data?.file?.isFolder,
    retry: false,
  });

  const folderInfo = folderData?.data;
  const share = data?.data;
  const errCode = (error as any)?.response?.data?.error?.code;
  const isExpired = errCode === 'SHARE_EXPIRED';
  const isExhausted = errCode === 'SHARE_DOWNLOAD_LIMIT_EXCEEDED';
  const needsPw = !isExpired && !isExhausted && !share && !isLoading && enteredPw === undefined;
  const wrongPw = !isExpired && !isExhausted && !share && !isLoading && enteredPw !== undefined;

  const isFolder = share?.file?.isFolder ?? false;
  const rootFile = share?.file;

  const children: ShareChildFile[] = currentFolderId ? (folderInfo?.children ?? []) : (share?.children ?? []);
  const fileChildren = children.filter((c) => !c.isFolder);
  const allSelected = fileChildren.length > 0 && fileChildren.every((c) => selectedIds.has(c.id));

  const canPreviewFile = (mimeType: string | null) => {
    return isPreviewable(mimeType);
  };

  const trigger = (url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(fileChildren.map((c) => c.id)));
  };
  const toggleOne = (id: string) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };

  const navigateToFolder = (folderId: string, folderName: string) => {
    setCurrentPath((prev) => [...prev, { id: folderId, name: folderName }]);
    setCurrentFolderId(folderId);
    setSelectedIds(new Set());
  };

  const navigateToPathIndex = (index: number) => {
    if (index === -1) {
      setCurrentFolderId(null);
      setCurrentPath([]);
    } else {
      const targetFolder = currentPath[index];
      if (targetFolder) {
        setCurrentPath((prev) => prev.slice(0, index));
        setCurrentFolderId(targetFolder.id);
      }
    }
    setSelectedIds(new Set());
  };

  const currentFolderName = currentFolderId
    ? (folderInfo?.folder?.name ?? currentPath[currentPath.length - 1]?.name)
    : rootFile?.name;

  return (
    <Shell title={t("文件分享")}>
      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p>{t("加载中...")}</p>
        </div>
      )}

      {isExpired && (
        <StatusCard
          icon={<Clock className="h-7 w-7 text-amber-500" />}
          bg="bg-amber-500/10"
          title={t("链接已过期")}
          desc={t("这个分享链接已超过有效期，无法访问。")}
        />
      )}
      {isExhausted && (
        <StatusCard
          icon={<AlertCircle className="h-7 w-7 text-red-500" />}
          bg="bg-red-500/10"
          title={t("下载次数已达上限")}
          desc={t("该分享链接的下载次数已用完。")}
        />
      )}

      {(needsPw || wrongPw) && <PasswordPrompt isWrong={wrongPw} onSubmit={(pw) => setEnteredPw(pw)} />}

      {/* Single file */}
      {share && !isFolder && (
        <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
          {share.file?.mimeType?.startsWith('image/') ? (
            <div
              className="bg-muted/30 border-b flex items-center justify-center p-6 min-h-[180px] cursor-pointer"
              onClick={() =>
                setPreviewFile({
                  id: shareId,
                  name: share.file!.name,
                  size: share.file!.size,
                  mimeType: share.file!.mimeType,
                })
              }
            >
              <img
                src={shareApi.previewUrl(shareId, enteredPw)}
                alt={decodeFileName(share.file.name)}
                className="max-h-48 max-w-full object-contain rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="bg-muted/30 border-b flex items-center justify-center py-10">
              <FileIcon mimeType={share.file?.mimeType} isFolder={false} size="lg" />
            </div>
          )}
          <div className="p-5 space-y-4">
            <div>
              <h1 className="font-semibold text-base break-all">{decodeFileName(share.file?.name)}</h1>
              <ShareMeta share={share} />
            </div>
            <div className="flex gap-2">
              {canPreviewFile(share.file?.mimeType ?? null) && (
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() =>
                    setPreviewFile({
                      id: shareId,
                      name: share.file!.name,
                      size: share.file!.size,
                      mimeType: share.file!.mimeType,
                    })
                  }
                >
                  <Eye className="h-4 w-4 mr-2" /> {t("预览")}</Button>
              )}
              <Button
                className={canPreviewFile(share.file?.mimeType ?? null) ? 'flex-1' : 'w-full'}
                onClick={() => trigger(shareApi.downloadUrl(shareId, enteredPw), share.file!.name)}
              >
                <Download className="h-4 w-4 mr-2" /> {t("下载")}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Folder */}
      {share && isFolder && (
        <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
          <div className="p-5 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <FolderOpen className="h-5 w-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-semibold text-base break-all">{currentFolderName ? decodeFileName(currentFolderName) : t('文件夹')}</h1>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
                  <span>{t("{{p0}} 个项目", { p0: children.length })}</span>
                  <ShareMeta share={share} inline />
                </div>
              </div>
            </div>
          </div>

          {/* Breadcrumb */}
          {(currentPath.length > 0 || currentFolderId) && (
            <div className="px-5 py-2 border-b bg-muted/10 flex items-center gap-1 text-sm overflow-x-auto">
              <button
                onClick={() => navigateToPathIndex(-1)}
                className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
              >
                <Home className="h-3.5 w-3.5" />
                <span>{decodeFileName(rootFile?.name ?? '')}</span>
              </button>
              {currentPath.map((item, index) => (
                <div key={item.id} className="flex items-center gap-1 flex-shrink-0">
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  {index === currentPath.length - 1 ? (
                    <span className="text-foreground font-medium">{decodeFileName(item.name)}</span>
                  ) : (
                    <button
                      onClick={() => navigateToPathIndex(index)}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {decodeFileName(item.name)}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Toolbar */}
          {fileChildren.length > 0 && (
            <div className="px-5 py-2.5 border-b bg-muted/20 flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={toggleAll}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {allSelected ? (
                  <CheckSquare className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Square className="h-3.5 w-3.5" />
                )}
                {allSelected ? t("取消全选") : t("全选")}
                {selectedIds.size > 0 && <span className="text-primary font-medium">{t("（已选 {{p0}}）", { p0: selectedIds.size })}</span>}
              </button>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1"
                    onClick={() =>
                      trigger(shareApi.zipUrl(shareId, enteredPw, [...selectedIds]), `${currentFolderName ?? '文件夹'}.zip`)
                    }
                  >
                    <Archive className="h-3 w-3" /> {t("下载所选 ({{p0}})", { p0: selectedIds.size })}</Button>
                )}
                <Button
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => trigger(shareApi.zipUrl(shareId, enteredPw), `${currentFolderName ?? '文件夹'}.zip`)}
                >
                  <Archive className="h-3.5 w-3.5" /> {t("全部 ZIP")}</Button>
              </div>
            </div>
          )}

          {/* List */}
          {folderLoading ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              {t("加载中...")}</div>
          ) : children.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">{t("文件夹为空")}</div>
          ) : (
            <div className="divide-y max-h-[480px] overflow-y-auto">
              {children.map((child) => (
                <div
                  key={child.id}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-accent/30 transition-colors group cursor-pointer"
                  onClick={() => {
                    if (child.isFolder) {
                      navigateToFolder(child.id, child.name);
                    }
                  }}
                >
                  {!child.isFolder ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(child.id);
                      }}
                      className="flex-shrink-0 text-muted-foreground hover:text-primary"
                    >
                      {selectedIds.has(child.id) ? (
                        <CheckSquare className="h-4 w-4 text-primary" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  ) : (
                    <div className="w-4 flex-shrink-0" />
                  )}

                  <FileIcon mimeType={child.mimeType} isFolder={child.isFolder} size="sm" />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate font-medium">{decodeFileName(child.name)}</p>
                    <p className="text-xs text-muted-foreground">
                      {child.isFolder ? t("文件夹") : formatBytes(child.size)}
                      <span className="mx-1.5">·</span>
                      {formatDate(child.updatedAt)}
                    </p>
                  </div>

                  {child.isFolder ? (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity touch-visible">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity touch-visible">
                      {canPreviewFile(child.mimeType) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewFile({
                              id: child.id,
                              name: child.name,
                              size: child.size,
                              mimeType: child.mimeType,
                            });
                          }}
                          className="flex-shrink-0 p-1.5 rounded hover:bg-accent"
                          title={t("预览")}
                        >
                          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          trigger(shareApi.childDownloadUrl(shareId, child.id, enteredPw), child.name);
                        }}
                        className="flex-shrink-0 p-1.5 rounded hover:bg-accent"
                        title={t("下载")}
                      >
                        <Download className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {previewFile && (
        <ShareFilePreview
          shareId={shareId}
          file={previewFile}
          password={enteredPw}
          isChildFile={previewFile.id !== shareId}
          onClose={() => setPreviewFile(null)}
          onDownload={() => {
            if (previewFile.id === shareId) {
              trigger(shareApi.downloadUrl(shareId, enteredPw), previewFile.name);
            } else {
              trigger(shareApi.childDownloadUrl(shareId, previewFile.id, enteredPw), previewFile.name);
            }
          }}
        />
      )}
    </Shell>
  );
}

function StatusCard({ icon, bg, title, desc }: { icon: React.ReactNode; bg: string; title: string; desc: string }) {
  return (
    <div className="bg-card border rounded-xl p-8 text-center space-y-4">
      <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center mx-auto`}>{icon}</div>
      <div>
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{desc}</p>
      </div>
    </div>
  );
}

function ShareMeta({ share, inline = false }: { share: any; inline?: boolean }) {
  const { t } = useI18n();
  const items = [
    share.file?.size != null && !share.file.isFolder && formatBytes(share.file.size),
    share.expiresAt && t("过期 {{p0}}", { p0: formatDate(share.expiresAt) }),
    share.downloadLimit != null && t("剩余 {{p0}} 次", { p0: Math.max(0, share.downloadLimit - share.downloadCount) }),
    share.hasPassword && t("已加密"),
  ].filter(Boolean);

  if (inline)
    return (
      <>
        {items.map((item, i) => (
          <span key={i}>{item}</span>
        ))}
      </>
    );

  return (
    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
      {items.map((item, i) => (
        <span key={i}>{item}</span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UploadLinkPage
// ─────────────────────────────────────────────────────────────────────────────
interface UploadEntry {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  error?: string;
}

function UploadLinkPage({ token }: { token: string }) {
  const { t } = useI18n();
  const [enteredPw, setEnteredPw] = useState<string | undefined>(undefined);
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['upload-link', token, enteredPw],
    queryFn: () => shareApi.getUploadLink(token, enteredPw).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const linkInfo: UploadLinkInfo | undefined = data?.data;
  const errCode = (error as any)?.response?.data?.error?.code;
  const isExpired = errCode === 'SHARE_EXPIRED';
  const needsPw = !isExpired && !linkInfo && !isLoading && enteredPw === undefined;
  const wrongPw = !isExpired && !linkInfo && !isLoading && enteredPw !== undefined;

  const addFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    setEntries((prev) => [
      ...prev,
      ...arr
        .filter((f) => !prev.some((e) => e.file.name === f.name && e.file.size === f.size))
        .map((f) => ({ file: f, status: 'pending' as const, progress: 0 })),
    ]);
  };

  const handleUploadAll = async () => {
    if (entries.every((e) => e.status !== 'pending')) return;
    setIsUploading(true);
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (!entry || entry.status !== 'pending') continue;
      setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, status: 'uploading' } : e)));
      try {
        await shareApi.uploadViaLink(token, entry.file, enteredPw, (pct) => {
          setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, progress: pct } : e)));
        });
        setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, status: 'done', progress: 100 } : e)));
      } catch (err: any) {
        const msg = err?.response?.data?.error?.message ?? err?.message ?? t("上传失败");
        setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, status: 'error', error: msg } : e)));
      }
    }
    setIsUploading(false);
  };

  const pendingCount = entries.filter((e) => e.status === 'pending').length;
  const doneCount = entries.filter((e) => e.status === 'done').length;

  return (
    <Shell title={t("文件上传")}>
      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p>{t("加载中...")}</p>
        </div>
      )}
      {isExpired && (
        <StatusCard
          icon={<Clock className="h-7 w-7 text-amber-500" />}
          bg="bg-amber-500/10"
          title={t("链接已过期")}
          desc={t("这个上传链接已超过有效期，无法访问。")}
        />
      )}
      {(needsPw || wrongPw) && <PasswordPrompt isWrong={wrongPw} onSubmit={(pw) => setEnteredPw(pw)} />}

      {linkInfo && (
        <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="p-5 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Upload className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t("上传到：{{p0}}", { p0: linkInfo.folderName })}</p>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
                  <span>{t("最大 {{p0}}", { p0: formatBytes(linkInfo.maxUploadSize) })}</span>
                  {linkInfo.maxUploadCount != null && (
                    <span>{t("剩余 {{p0}} 次", { p0: Math.max(0, linkInfo.maxUploadCount - linkInfo.uploadCount) })}</span>
                  )}
                  {linkInfo.expiresAt && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="h-3 w-3" /> {t("过期 {{p0}}", { p0: formatDate(linkInfo.expiresAt) })}</span>
                  )}
                  {linkInfo.allowedMimeTypes?.length && <span>{t("限 {{p0}}", { p0: linkInfo.allowedMimeTypes.join(', ') })}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <div
            className="p-5 border-b"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
            }}
          >
            <button
              onClick={() => inputRef.current?.click()}
              disabled={isUploading}
              className="w-full rounded-lg border-2 border-dashed border-border hover:border-primary/60 transition-colors py-8 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <File className="h-7 w-7 opacity-40" />
              <span className="text-sm font-medium">{t("点击或拖放文件到此处")}</span>
              <span className="text-xs opacity-60">{t("支持多文件同时上传")}</span>
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && addFiles(e.target.files)}
            />
          </div>

          {/* Queue */}
          {entries.length > 0 && (
            <div className="divide-y max-h-64 overflow-y-auto">
              {entries.map((entry, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex-shrink-0 w-4">
                    {entry.status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                    {entry.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                    {entry.status === 'uploading' && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                    {entry.status === 'pending' && <File className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{decodeFileName(entry.file.name)}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-muted-foreground">{formatBytes(entry.file.size)}</p>
                      {entry.status === 'uploading' && (
                        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${entry.progress}%` }}
                          />
                        </div>
                      )}
                      {entry.status === 'error' && <p className="text-xs text-red-500 truncate">{t(entry.error || '')}</p>}
                      {entry.status === 'done' && <p className="text-xs text-emerald-500">{t("上传成功")}</p>}
                    </div>
                  </div>
                  {entry.status === 'pending' && !isUploading && (
                    <button
                      onClick={() => setEntries((prev) => prev.filter((_, idx) => idx !== i))}
                      className="flex-shrink-0 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="p-5 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {entries.length === 0
                ? t("尚未选择文件")
                : [doneCount > 0 ? t("{{p0}} 个已完成", { p0: doneCount }) : '', pendingCount > 0 ? t('{{p0}} 个待上传', { p0: pendingCount }) : ''].filter(Boolean).join(' · ')}
            </p>
            <Button onClick={handleUploadAll} disabled={pendingCount === 0 || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("上传中...")}</>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" /> {t("开始上传")}{pendingCount > 0 && ` (${pendingCount})`}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </Shell>
  );
}
